import { reactive } from 'vue';
// @ts-expect-error - auth0-js has no type definitions
import auth0 from 'auth0-js';
import { navigateForContext } from '@/plugins/context-navigation.ts';

/**
 * Handles MFA enroll/remove redirects with Auth0.
 * MFA means Multi-Factor Authentication.
 * Stores temporary MFA state and navigates back to the original route.
 */
interface MfaAuthData {
    accessToken: string | null;
    action: string | null;
}

interface MfaAuthAppState {
    action?: string;
    target?: string;
}

interface MfaAuthResult {
    accessToken?: string;
    appState?: MfaAuthAppState;
}

type RouterPushPayload = {
    path: string;
};

type RouterLike = {
    push: (payload: RouterPushPayload) => unknown;
};

type NotifyLike = {
    warning: (message: string) => void;
};

type TranslateLike = (key: string) => string;

interface MfaDependencies {
    router?: RouterLike | null;
    notify?: NotifyLike | null;
    translate?: TranslateLike | null;
}

const authClient = new auth0.WebAuth({
    domain: import.meta.env.VITE_AUTH0_DOMAIN,
    redirectUri: `${window.location.origin}/auth/mfa-callback`,
    clientID: import.meta.env.VITE_AUTH0_CLIENT_ID,
    audience: import.meta.env.VITE_AUTH0_MFA_AUDIENCE,
    responseType: 'token',
    scope: 'enroll read:authenticators remove:authenticators',
});

class MfaAuth {
    data: MfaAuthData;

    router: RouterLike | null;

    notify: NotifyLike | null;

    translate: TranslateLike | null;

    constructor() {
        this.data = reactive<MfaAuthData>({
            accessToken: null,
            action: null,
        });
        this.router = null;
        this.notify = null;
        this.translate = null;
    }

    setDependencies({
        router = null,
        notify = null,
        translate = null,
    }: MfaDependencies = {}): void {
        this.router = router;
        this.notify = notify;
        this.translate = translate;
    }

    enroll(): void {
        this.generateMfaToken('enroll');
    }

    remove(): void {
        this.generateMfaToken('remove');
    }

    generateMfaToken(action: string): void {
        const payload: Record<string, unknown> = {
            appState: {
                target: `${window.location.pathname}${window.location.search}`,
                action,
            },
            connection: import.meta.env.VITE_AUTH0_CONNECTION,
        };

        const locale = localStorage.getItem('currentLocale');
        if (locale) {
            payload.ui_locales = locale;
        }

        authClient.authorize(payload);
    }

    handleAuthentication(): Promise<void> {
        return new Promise((resolve) => {
            authClient.parseHash((error: unknown, authResult: MfaAuthResult | null) => {
                if (error) {
                    if (this.notify?.warning && this.translate) {
                        this.notify.warning(this.translate('users.alerts.mfaEnrollment.error'));
                    }

                    resolve();
                    return;
                }

                if (!authResult) {
                    resolve();
                    return;
                }

                this.data.action = authResult.appState?.action ?? null;
                this.data.accessToken = authResult.accessToken ?? null;

                this.redirectToPreviousState(authResult).catch(() => {});
                resolve();
            });
        });
    }

    redirectToPreviousState(authResult: MfaAuthResult): Promise<void> {
        const target = authResult.appState?.target || '/';

        if (this.router?.push) {
            const router = this.router;
            return navigateForContext(target, (destination) => router.push(destination));
        }

        window.location.assign(target);
        return Promise.resolve();
    }

    clear(): void {
        this.data.accessToken = null;
        this.data.action = null;
    }
}

export type { RouterLike, NotifyLike, TranslateLike };
export default MfaAuth;
